/**
 * --------------------------------------------------------------------------
 * CoreUI PRO data-grid engine/active-cell.ts
 * License (https://coreui.io/pro/license/)
 * --------------------------------------------------------------------------
 */

// The cellNavigation substrate: one grid-owned active cell (roving tabindex).
// rowIndex is an index into the current row model (page-local under
// pagination); -1 addresses the header label row. Focus is derived from this
// state, never from the DOM - the body applies it after every render pass.

export interface ActiveCell {
  rowIndex: number
  columnId: string
}

export interface ActiveCellContext {
  // Visible leaf column ids in visual order (pinning + order + visibility).
  columnIds: string[]
  rowCount: number
  // Rows per PageUp/PageDown step: visible viewport rows (virtualized) or the
  // page size (paginated).
  pageStep: number
  paginated: boolean
  // Writing direction of the grid. Arrow keys are physical, column order is
  // logical, so under RTL the two horizontal keys trade places.
  rtl?: boolean
}

export interface NavigationResult {
  cell: ActiveCell
  pageDelta?: -1 | 1
}

const HEADER_ROW_INDEX = -1

const clampColumn = (columnId: string, columnIds: string[]): string | null => {
  if (columnIds.includes(columnId)) {
    return columnId
  }

  return columnIds.length > 0 ? columnIds[0] : null
}

// Nulls the cell when the grid has no addressable cells; otherwise snaps it
// back into the current column set and row range.
export const clampActiveCell = (cell: ActiveCell | null, context: ActiveCellContext): ActiveCell | null => {
  if (!cell) {
    return null
  }

  const columnId = clampColumn(cell.columnId, context.columnIds)
  if (columnId === null) {
    return null
  }

  const maxRow = context.rowCount - 1
  const rowIndex = cell.rowIndex < 0 ?
    HEADER_ROW_INDEX :
    Math.max(0, Math.min(cell.rowIndex, Math.max(maxRow, 0)))

  if (rowIndex >= 0 && maxRow < 0) {
    return { rowIndex: HEADER_ROW_INDEX, columnId }
  }

  return { rowIndex, columnId }
}

// The APG key decision table - a pure function so every edition asserts
// identical outcomes. Returns null for keys the grid does not handle.
export const resolveNavigation = (
  key: string,
  ctrlKey: boolean,
  current: ActiveCell,
  context: ActiveCellContext
): NavigationResult | null => {
  const { columnIds, rowCount, pageStep, paginated, rtl } = context
  const columnIndex = columnIds.indexOf(current.columnId)
  if (columnIndex === -1 || columnIds.length === 0) {
    return null
  }

  const lastRow = rowCount - 1
  const lastColumn = columnIds.length - 1
  const cell = (rowIndex: number, index: number): NavigationResult => ({
    cell: { rowIndex, columnId: columnIds[index] }
  })

  switch (key) {
    case 'ArrowLeft': {
      return rtl ?
        cell(current.rowIndex, Math.min(columnIndex + 1, lastColumn)) :
        cell(current.rowIndex, Math.max(columnIndex - 1, 0))
    }

    case 'ArrowRight': {
      return rtl ?
        cell(current.rowIndex, Math.max(columnIndex - 1, 0)) :
        cell(current.rowIndex, Math.min(columnIndex + 1, lastColumn))
    }

    case 'ArrowUp': {
      return cell(Math.max(current.rowIndex - 1, HEADER_ROW_INDEX), columnIndex)
    }

    case 'ArrowDown': {
      return cell(Math.min(current.rowIndex + 1, lastRow), columnIndex)
    }

    case 'Home': {
      if (ctrlKey) {
        return cell(rowCount > 0 ? 0 : HEADER_ROW_INDEX, 0)
      }

      return cell(current.rowIndex, 0)
    }

    case 'End': {
      if (ctrlKey) {
        return cell(rowCount > 0 ? lastRow : HEADER_ROW_INDEX, lastColumn)
      }

      return cell(current.rowIndex, lastColumn)
    }

    case 'PageUp': {
      if (paginated && current.rowIndex === 0) {
        return { ...cell(0, columnIndex), pageDelta: -1 }
      }

      return cell(Math.max(current.rowIndex - pageStep, 0), columnIndex)
    }

    case 'PageDown': {
      if (paginated && current.rowIndex === lastRow) {
        return { ...cell(lastRow, columnIndex), pageDelta: 1 }
      }

      return cell(Math.min(current.rowIndex + pageStep, lastRow), columnIndex)
    }

    default: {
      return null
    }
  }
}

export interface ActiveCellManager {
  get: () => ActiveCell | null
  // Stores the cell; focus: true marks a pending focus request the next
  // render pass consumes (set false for pointer-driven activation, where the
  // browser already moved focus).
  set: (cell: ActiveCell, options?: { focus?: boolean }) => void
  clamp: (context: ActiveCellContext) => void
  reset: () => void
  consumeFocusRequest: () => boolean
}

export const createActiveCellManager = (onChange: () => void): ActiveCellManager => {
  let current: ActiveCell | null = null
  let focusRequested = false

  return {
    get: () => current,
    set(cell, options = {}) {
      const changed = !current || current.rowIndex !== cell.rowIndex || current.columnId !== cell.columnId
      current = cell
      if (options.focus) {
        focusRequested = true
      }

      if (changed || options.focus) {
        onChange()
      }
    },
    clamp(context) {
      const next = clampActiveCell(current, context)
      const changed = Boolean(current) !== Boolean(next) ||
        (current && next && (current.rowIndex !== next.rowIndex || current.columnId !== next.columnId))
      current = next
      if (changed) {
        onChange()
      }
    },
    reset() {
      if (current) {
        current = null
        focusRequested = false
        onChange()
      }
    },
    consumeFocusRequest() {
      if (!focusRequested) {
        return false
      }

      focusRequested = false
      return true
    }
  }
}
